import { PAGE_SIZES } from "@cleanbudget/contract/list";
import { type ProductListItem, ProductListQuery, ProductStatusFilter } from "@cleanbudget/contract/product";
import { Button } from "@cleanbudget/ui/components/button";
import { IconArrowBackUp, IconPlus } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ButtonLink } from "@/components/button-link";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { DataTableEmpty } from "@/components/data-table/empty";
import { DataTablePagination } from "@/components/data-table/pagination";
import { DataTableToolbar, FilterSelect, SearchInput } from "@/components/data-table/toolbar";
import { PageHeader } from "@/components/page-header";
import { formatDate } from "@/lib/format";
import { formatCents } from "@/lib/money";
import { orpc } from "@/lib/orpc";
import { useRestoreProduct } from "./-components/delete-product";
import { ProductStatusBadge } from "./-components/product-status";

/**
 * The list's URL: the contract's own query, made forgiving, because a URL is typed by hand too
 * (`?page=abc` is page 1, an unknown status is "all").
 */
const ProductListSearch = z.object({
	search: ProductListQuery.shape.search.catch(undefined),
	status: ProductStatusFilter.default("all").catch("all"),
	page: z.coerce.number().int().min(1).max(100_000).default(1).catch(1),
	pageSize: z.coerce.number().pipe(z.literal(PAGE_SIZES)).default(25).catch(25),
});
type ProductListSearch = z.infer<typeof ProductListSearch>;

const productsQuery = (search: ProductListSearch) => orpc.product.list.queryOptions({ input: search });

/** The defaults, left out of the URL so a plain `/product` is the default view. */
const defaults = ProductListSearch.parse({});

/**
 * Producten (#49): the data table, searchable on name and SKU, filtered on active, sorted by name.
 * Archived products (spec, A2) only show under the filter "Gearchiveerd", each with "Terugzetten".
 * Search, filter and page live in the URL. "Nieuw product" leads to the short form that creates
 * one and opens its editor. Groups, attributes and holders (#160), which the editor assigns, have a
 * page each, linked from the header.
 */
export const Route = createFileRoute("/_admin/product/")({
	validateSearch: ProductListSearch,
	search: { middlewares: [stripSearchParams(defaults)] },
	loaderDeps: ({ search }) => search,
	loader: ({ context, deps }) => context.queryClient.ensureQueryData(productsQuery(deps)),
	component: ProductsPage,
});

function ProductsPage() {
	const { t } = useTranslation("product");
	const { t: tBundle } = useTranslation("bundle");
	const { t: tCategory } = useTranslation("category");
	const { t: tAttribute } = useTranslation("attribute");
	const { t: tHolder } = useTranslation("holder");
	const search = Route.useSearch();
	const { data } = useSuspenseQuery(productsQuery(search));

	const price = (product: ProductListItem) => {
		const { minConsumerPrice: min, maxConsumerPrice: max } = product;
		if (min === null || max === null) return null;
		return min === max ? formatCents(min) : t("priceRange", { min: formatCents(min), max: formatCents(max) });
	};

	const columns: readonly Column<ProductListItem>[] = [
		{
			id: "name",
			header: t("column.name"),
			cell: (product) => (
				<span className="flex flex-col">
					<Link
						to="/product/$productId"
						params={{ productId: product.id }}
						className="font-medium underline-offset-4 hover:underline"
					>
						{product.name}
					</Link>
					{product.kind === "bundle" ? (
						<span className="text-xs text-muted-foreground">{tBundle("listBadge")}</span>
					) : null}
					{/* On a phone the SKU and price columns are hidden; show both under the name. */}
					<span className="text-xs text-muted-foreground md:hidden">
						{[product.sku ?? t("variantCount", { count: product.variantCount }), price(product)]
							.filter(Boolean)
							.join(" · ")}
					</span>
				</span>
			),
		},
		{
			id: "sku",
			header: t("column.sku"),
			secondary: true,
			cell: (product) =>
				product.sku ? (
					<span className="font-mono text-xs">{product.sku}</span>
				) : (
					<span className="text-muted-foreground">{t("variantCount", { count: product.variantCount })}</span>
				),
		},
		{
			id: "price",
			header: t("column.price"),
			secondary: true,
			align: "right",
			cell: (product) => <span className="tabular-nums">{price(product)}</span>,
		},
		{
			id: "status",
			header: t("column.status"),
			cell: (product) => <ProductStatusBadge product={product} />,
		},
		{
			id: "updatedAt",
			header: t("column.updated"),
			secondary: true,
			cell: (product) => <time dateTime={product.updatedAt}>{formatDate(product.updatedAt)}</time>,
		},
		...(search.status === "archived"
			? [
					{
						id: "restore",
						header: <span className="sr-only">{t("archived.restore")}</span>,
						align: "right",
						cell: (product) => <RestoreButton product={product} />,
					} satisfies Column<ProductListItem>,
				]
			: []),
	];

	const filtered = Boolean(search.search) || search.status !== "all";
	const pastEnd = data.items.length === 0 && data.total > 0;

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title={t("title")}
				description={t("description")}
				actions={
					<>
						<nav aria-label={t("catalogLinks.label")} className="flex flex-wrap gap-2">
							<ButtonLink to="/product/category" variant="outline">
								{tCategory("title")}
							</ButtonLink>
							<ButtonLink to="/product/attribute" variant="outline">
								{tAttribute("title")}
							</ButtonLink>
							<ButtonLink to="/product/holder" variant="outline">
								{tHolder("title")}
							</ButtonLink>
						</nav>
						<ButtonLink to="/product/new">
							<IconPlus aria-hidden />
							{t("new.title")}
						</ButtonLink>
					</>
				}
			/>
			<div className="flex flex-col gap-3">
				<DataTableToolbar>
					<SearchInput value={search.search} label={t("searchLabel")} />
					<FilterSelect
						param="status"
						label={t("status.label")}
						value={search.status === "all" ? undefined : search.status}
						options={[
							{ value: "active", label: t("status.active") },
							{ value: "inactive", label: t("status.inactive") },
							{ value: "archived", label: t("status.archived") },
						]}
					/>
				</DataTableToolbar>
				<DataTable
					label={t("title")}
					columns={columns}
					rows={data.items}
					rowKey={(product) => product.id}
					empty={<DataTableEmpty filtered={filtered} pastEnd={pastEnd} nothingYet={t("empty")} />}
				/>
				<DataTablePagination page={data.page} pageSize={data.pageSize} total={data.total} />
			</div>
		</div>
	);
}

/** "Terugzetten" on a row of the archived list: the row leaves this list. */
function RestoreButton({ product }: { product: ProductListItem }) {
	const { t } = useTranslation("product");
	const restore = useRestoreProduct();
	return (
		<Button
			type="button"
			size="sm"
			variant="outline"
			disabled={restore.isPending}
			onClick={() => restore.mutate({ productId: product.id })}
			aria-label={t("archived.restoreNamed", { name: product.name })}
		>
			<IconArrowBackUp aria-hidden />
			{t("archived.restore")}
		</Button>
	);
}
